/**
 * Bilingual content for LUKHΛS Passphrase Coach
 * Comprehensive i18n support for EN and ES
 */

export const passphraseContent = {
  en: {
    // Helper text (inline helpers)
    helpers: {
      fillerExplanation:
        'Connectors like "-", "Y", "SE" make your passphrase easier to read and remember, but add almost no security. Focus on strong words and emoji for protection.',
      focusOnWords:
        'Focus on selecting random words and emoji. These provide the real security for your passphrase.',
      neverStore:
        'We never store your passphrase. It exists only in your browser and is never sent to any server.',
    },

    // Labels and UI strings
    labels: {
      entropyMeter: 'Estimated entropy: {bits} bits',
      weak: 'Weak',
      ok: 'OK',
      strong: 'Strong',
      veryStrong: 'Very Strong',
      tokenTypes: {
        WORD: 'Word',
        EMOJI: 'Emoji',
        FILLER: 'Connector',
        DIGIT: 'Digit',
        CHAR: 'Character',
      },
      tooltips: {
        WORD: 'Random words are the foundation of a strong passphrase. Each word from a large dictionary adds significant entropy.',
        EMOJI: 'Emoji add visual memorability and significant entropy. Each emoji from thousands of options strengthens your passphrase.',
        FILLER: 'Connectors like hyphens or words like "Y", "SE" help readability but add minimal security. They\'re useful for remembering the structure.',
        DIGIT: 'Random digits add some entropy, but are less effective than words or emoji for the same length.',
        CHAR: 'Special characters add entropy but can be harder to type. Consider using words or emoji instead for better memorability.',
      },
    },

    // Modal content (education)
    modal: {
      title: 'What is a passphrase and how should I protect it?',
      intro:
        'Your passphrase is the master key to your LUKHΛS identity. It is generated and stored entirely on your device—we never see it, store it, or transmit it to any server. This means you have complete control and complete responsibility.',

      sections: {
        whyPassphrase: {
          title: 'Why use a passphrase instead of a password?',
          content:
            'Passphrases are longer, more memorable, and significantly more secure than traditional passwords. A combination of 4-6 random words with emoji creates a phrase that\'s both human-friendly and cryptographically strong. For example: "turtle-🌙-whisper-mountain-⚡-garden" is far stronger than "P@ssw0rd123!" and easier to remember.',
        },

        howToCreate: {
          title: 'How to create a strong passphrase',
          content:
            'The most secure passphrases combine 4-6 random words with emoji. Each word should be chosen randomly from a large dictionary—avoid meaningful sentences or personal information. Add emoji to increase entropy and create visual memory anchors. Use simple connectors (hyphens, "Y", "SE") only for readability; they add minimal security.',
          format: '**Recommended format:** WORD-EMOJI-WORD-connector-WORD-EMOJI-WORD',
          example:
            '**Example:** "👦🏼-🐈-SE-🗣️-BRULA-Y-✈️-MADRID-EN-🎄" (a story: "A boy and his cat speak gibberish and fly to Madrid for Christmas")',
        },

        whereNotToStore: {
          title: 'Where NOT to store your passphrase',
          items: [
            '❌ Email, text messages, or unencrypted messaging apps',
            '❌ Screenshots or photos on your phone',
            '❌ Unencrypted notes apps (Apple Notes, Google Keep, Evernote)',
            '❌ Browser autofill or password managers without master passwords',
            '❌ Cloud storage without encryption (Dropbox, Google Drive)',
            '❌ Paper in easily accessible locations',
          ],
        },

        whereToStore: {
          title: 'Where TO store your passphrase',
          items: [
            '✅ **Password managers** with strong master passwords (1Password, Bitwarden, KeePass)',
            '✅ **Physical safe or safety deposit box** (for paper backups)',
            '✅ **Encrypted USB drive** stored securely offline',
            '✅ **Hardware security keys** or offline storage devices',
            '✅ **Multiple secure locations** (diversify your backup strategy)',
          ],
          recommendation:
            '**Recommendation:** Use a combination of digital (encrypted password manager) and physical (safe) storage. Never rely on a single backup method.',
        },

        recoveryOptions: {
          title: 'LUKHΛS Recovery Options',
          intro:
            'LUKHΛS offers multiple recovery strategies to balance security and accessibility:',
          primary: {
            title: '1. Primary Recovery: Passphrase + Recovery Package',
            content:
              'Your passphrase combined with your encrypted recovery package (downloaded during setup) allows full account recovery. Store them separately for maximum security.',
          },
          optional: {
            title: '2. Optional: Trusted Guardians',
            content:
              'Distribute encrypted recovery shards to trusted contacts. If you lose access, a threshold of guardians can help you recover (e.g., 3 out of 5).',
          },
          fallback: {
            title: '3. Fallback: Email Recovery',
            content:
              'If enabled, you can request a time-locked recovery link to your verified email. This provides a safety net but slightly reduces security. Use only if you\'re confident in your email account\'s security.',
          },
          warning:
            '⚠️ **Important:** No recovery method works without your passphrase or recovery package. If you lose both, your account cannot be recovered—even by LUKHΛS.',
        },

        bestPractices: {
          title: 'Best Practices Checklist',
          items: [
            '✓ Use 4-6 random words from a large dictionary',
            '✓ Add emoji for extra entropy and visual memory',
            '✓ Avoid personal information (names, dates, addresses)',
            '✓ Never reuse passphrases from other accounts',
            '✓ Store your passphrase and recovery package separately',
            '✓ Download and verify your recovery package immediately',
            '✓ Test your backups periodically',
            '✓ Update your passphrase if you suspect it\'s compromised',
            '✓ Consider using a password manager for digital storage',
            '✓ Keep at least one offline backup in a secure location',
          ],
        },

        securityNotice: {
          title: 'Final Security Notice',
          content:
            '🔒 **Your passphrase is the only key to your LUKHΛS identity.** We use end-to-end encryption, which means we cannot reset your passphrase or recover your account for you. This is by design—it ensures that only you have access to your data. Treat your passphrase and recovery package like you would treat the keys to your home or your bank account.',
        },
      },
    },

    // Example phrases (educational only)
    examples: {
      title: 'Example Passphrases',
      warning: '⚠️ EXAMPLES ONLY — DO NOT REUSE THESE EXACT PHRASES',
      items: [
        {
          phrase: 'cat-dog-run',
          bits: 28,
          strength: 'weak',
          explanation:
            'Too short (only 3 words) and uses common words. Vulnerable to dictionary attacks.',
        },
        {
          phrase: 'correct-horse-battery-staple',
          bits: 44,
          strength: 'ok',
          explanation:
            'Famous XKCD example. 4 words is better, but this exact phrase is widely known and should never be used.',
        },
        {
          phrase: 'whisper-🌙-mountain-garden-⚡-river',
          bits: 77,
          strength: 'strong',
          explanation:
            '6 tokens (4 words + 2 emoji) from large dictionaries. Excellent balance of security and memorability.',
        },
        {
          phrase: 'velvet-🦋-cascade-Y-thunder-SE-✨-twilight',
          bits: 82,
          strength: 'veryStrong',
          explanation:
            '8 tokens with connectors "Y" and "SE" for readability. Very strong entropy from random words and emoji.',
        },
        {
          phrase: '👦🏼-🐈-SE-🗣️-BRULA-Y-✈️-MADRID-EN-🎄',
          bits: 95,
          strength: 'veryStrong',
          explanation:
            'Story-based passphrase: "A boy and his cat speak gibberish and fly to Madrid for Christmas". Combines emoji, random words, and place names with connectors for a memorable narrative.',
        },
      ],
    },

    // Button labels
    buttons: {
      continueButton: 'Continue',
      backButton: 'Back',
      applyChange: 'Apply Change',
      explainScore: 'Explain Score',
      learnMore: 'Learn More',
      downloadPackage: 'Download Recovery Package',
      regenerate: 'Regenerate',
      copyToClipboard: 'Copy to Clipboard',
      showPassphrase: 'Show Passphrase',
      hidePassphrase: 'Hide Passphrase',
    },

    // Additional UI strings
    ui: {
      passphraseStrength: 'Passphrase Strength',
      estimatedCrackTime: 'Estimated time to crack:',
      crackTimes: {
        instant: 'Instant',
        seconds: '{time} seconds',
        minutes: '{time} minutes',
        hours: '{time} hours',
        days: '{time} days',
        months: '{time} months',
        years: '{time} years',
        centuries: 'Centuries',
      },
      copied: 'Copied to clipboard!',
      downloadSuccess: 'Recovery package downloaded successfully',
      placeholders: {
        enterPassphrase: 'Enter your passphrase...',
        confirmPassphrase: 'Confirm your passphrase...',
      },
    },
  },

  es: {
    // Texto de ayuda (ayudantes en línea)
    helpers: {
      fillerExplanation:
        'Los conectores como "-", "Y", "SE" hacen que tu frase de contraseña sea más fácil de leer y recordar, pero añaden casi ninguna seguridad. Enfócate en palabras fuertes y emoji para protección.',
      focusOnWords:
        'Enfócate en seleccionar palabras y emoji aleatorios. Estos proporcionan la seguridad real para tu frase de contraseña.',
      neverStore:
        'Nunca almacenamos tu frase de contraseña. Existe solo en tu navegador y nunca se envía a ningún servidor.',
    },

    // Etiquetas y cadenas de UI
    labels: {
      entropyMeter: 'Entropía estimada: {bits} bits',
      weak: 'Débil',
      ok: 'Aceptable',
      strong: 'Fuerte',
      veryStrong: 'Muy Fuerte',
      tokenTypes: {
        WORD: 'Palabra',
        EMOJI: 'Emoji',
        FILLER: 'Conector',
        DIGIT: 'Dígito',
        CHAR: 'Carácter',
      },
      tooltips: {
        WORD: 'Las palabras aleatorias son la base de una frase de contraseña fuerte. Cada palabra de un diccionario grande añade entropía significativa.',
        EMOJI: 'Los emoji añaden memorabilidad visual y entropía significativa. Cada emoji de miles de opciones fortalece tu frase de contraseña.',
        FILLER: 'Los conectores como guiones o palabras como "Y", "SE" ayudan a la legibilidad pero añaden seguridad mínima. Son útiles para recordar la estructura.',
        DIGIT: 'Los dígitos aleatorios añaden algo de entropía, pero son menos efectivos que palabras o emoji para la misma longitud.',
        CHAR: 'Los caracteres especiales añaden entropía pero pueden ser más difíciles de escribir. Considera usar palabras o emoji para mejor memorabilidad.',
      },
    },

    // Contenido del modal (educación)
    modal: {
      title: '¿Qué es una frase de contraseña y cómo debo protegerla?',
      intro:
        'Tu frase de contraseña es la llave maestra de tu identidad LUKHΛS. Se genera y almacena completamente en tu dispositivo—nunca la vemos, almacenamos o transmitimos a ningún servidor. Esto significa que tienes control completo y responsabilidad completa.',

      sections: {
        whyPassphrase: {
          title: '¿Por qué usar una frase de contraseña en lugar de una contraseña?',
          content:
            'Las frases de contraseña son más largas, más memorables y significativamente más seguras que las contraseñas tradicionales. Una combinación de 4-6 palabras aleatorias con emoji crea una frase que es tanto amigable para humanos como criptográficamente fuerte. Por ejemplo: "tortuga-🌙-susurro-montaña-⚡-jardín" es mucho más fuerte que "P@ssw0rd123!" y más fácil de recordar.',
        },

        howToCreate: {
          title: 'Cómo crear una frase de contraseña fuerte',
          content:
            'Las frases de contraseña más seguras combinan 4-6 palabras aleatorias con emoji. Cada palabra debe elegirse aleatoriamente de un diccionario grande—evita oraciones significativas o información personal. Añade emoji para aumentar la entropía y crear anclas de memoria visual. Usa conectores simples (guiones, "Y", "SE") solo para legibilidad; añaden seguridad mínima.',
          format: '**Formato recomendado:** PALABRA-EMOJI-PALABRA-conector-PALABRA-EMOJI-PALABRA',
          example:
            '**Ejemplo:** "👦🏼-🐈-SE-🗣️-BRULA-Y-✈️-MADRID-EN-🎄" (una historia: "Un niño y su gato hablan galimatías y vuelan a Madrid en Navidad")',
        },

        whereNotToStore: {
          title: 'Dónde NO almacenar tu frase de contraseña',
          items: [
            '❌ Email, mensajes de texto o apps de mensajería sin cifrar',
            '❌ Capturas de pantalla o fotos en tu teléfono',
            '❌ Apps de notas sin cifrar (Apple Notes, Google Keep, Evernote)',
            '❌ Autocompletar del navegador o gestores de contraseñas sin contraseña maestra',
            '❌ Almacenamiento en la nube sin cifrado (Dropbox, Google Drive)',
            '❌ Papel en ubicaciones fácilmente accesibles',
          ],
        },

        whereToStore: {
          title: 'Dónde SÍ almacenar tu frase de contraseña',
          items: [
            '✅ **Gestores de contraseñas** con contraseñas maestras fuertes (1Password, Bitwarden, KeePass)',
            '✅ **Caja fuerte física o caja de seguridad bancaria** (para copias en papel)',
            '✅ **USB cifrado** almacenado de forma segura sin conexión',
            '✅ **Llaves de seguridad de hardware** o dispositivos de almacenamiento sin conexión',
            '✅ **Múltiples ubicaciones seguras** (diversifica tu estrategia de respaldo)',
          ],
          recommendation:
            '**Recomendación:** Usa una combinación de almacenamiento digital (gestor de contraseñas cifrado) y físico (caja fuerte). Nunca confíes en un solo método de respaldo.',
        },

        recoveryOptions: {
          title: 'Opciones de Recuperación de LUKHΛS',
          intro:
            'LUKHΛS ofrece múltiples estrategias de recuperación para equilibrar seguridad y accesibilidad:',
          primary: {
            title: '1. Recuperación Principal: Frase de Contraseña + Paquete de Recuperación',
            content:
              'Tu frase de contraseña combinada con tu paquete de recuperación cifrado (descargado durante la configuración) permite la recuperación completa de la cuenta. Almacénalos por separado para máxima seguridad.',
          },
          optional: {
            title: '2. Opcional: Guardianes de Confianza',
            content:
              'Distribuye fragmentos de recuperación cifrados a contactos de confianza. Si pierdes el acceso, un umbral de guardianes puede ayudarte a recuperar (por ejemplo, 3 de 5).',
          },
          fallback: {
            title: '3. Respaldo: Recuperación por Email',
            content:
              'Si está habilitado, puedes solicitar un enlace de recuperación con bloqueo temporal a tu email verificado. Esto proporciona una red de seguridad pero reduce ligeramente la seguridad. Usa solo si confías en la seguridad de tu cuenta de email.',
          },
          warning:
            '⚠️ **Importante:** Ningún método de recuperación funciona sin tu frase de contraseña o paquete de recuperación. Si pierdes ambos, tu cuenta no puede recuperarse—ni siquiera por LUKHΛS.',
        },

        bestPractices: {
          title: 'Lista de Mejores Prácticas',
          items: [
            '✓ Usa 4-6 palabras aleatorias de un diccionario grande',
            '✓ Añade emoji para entropía extra y memoria visual',
            '✓ Evita información personal (nombres, fechas, direcciones)',
            '✓ Nunca reutilices frases de contraseña de otras cuentas',
            '✓ Almacena tu frase de contraseña y paquete de recuperación por separado',
            '✓ Descarga y verifica tu paquete de recuperación inmediatamente',
            '✓ Prueba tus respaldos periódicamente',
            '✓ Actualiza tu frase de contraseña si sospechas que está comprometida',
            '✓ Considera usar un gestor de contraseñas para almacenamiento digital',
            '✓ Mantén al menos un respaldo sin conexión en una ubicación segura',
          ],
        },

        securityNotice: {
          title: 'Aviso de Seguridad Final',
          content:
            '🔒 **Tu frase de contraseña es la única llave de tu identidad LUKHΛS.** Usamos cifrado de extremo a extremo, lo que significa que no podemos restablecer tu frase de contraseña o recuperar tu cuenta por ti. Esto es por diseño—asegura que solo tú tengas acceso a tus datos. Trata tu frase de contraseña y paquete de recuperación como tratarías las llaves de tu casa o tu cuenta bancaria.',
        },
      },
    },

    // Frases de ejemplo (solo educativas)
    examples: {
      title: 'Ejemplos de Frases de Contraseña',
      warning: '⚠️ SOLO EJEMPLOS — NO REUTILICES ESTAS FRASES EXACTAS',
      items: [
        {
          phrase: 'gato-perro-correr',
          bits: 28,
          strength: 'weak',
          explanation:
            'Demasiado corta (solo 3 palabras) y usa palabras comunes. Vulnerable a ataques de diccionario.',
        },
        {
          phrase: 'correcto-caballo-bateria-grapa',
          bits: 44,
          strength: 'ok',
          explanation:
            'Famoso ejemplo de XKCD. 4 palabras es mejor, pero esta frase exacta es ampliamente conocida y nunca debe usarse.',
        },
        {
          phrase: 'susurro-🌙-montaña-jardín-⚡-río',
          bits: 77,
          strength: 'strong',
          explanation:
            '6 tokens (4 palabras + 2 emoji) de diccionarios grandes. Excelente balance de seguridad y memorabilidad.',
        },
        {
          phrase: 'terciopelo-🦋-cascada-Y-trueno-SE-✨-crepúsculo',
          bits: 82,
          strength: 'veryStrong',
          explanation:
            '8 tokens con conectores "Y" y "SE" para legibilidad. Entropía muy fuerte de palabras y emoji aleatorios.',
        },
        {
          phrase: '👦🏼-🐈-SE-🗣️-BRULA-Y-✈️-MADRID-EN-🎄',
          bits: 95,
          strength: 'veryStrong',
          explanation:
            'Frase de contraseña basada en historia: "Un niño y su gato hablan galimatías y vuelan a Madrid en Navidad". Combina emoji, palabras aleatorias y nombres de lugares con conectores para una narrativa memorable.',
        },
      ],
    },

    // Etiquetas de botones
    buttons: {
      continueButton: 'Continuar',
      backButton: 'Atrás',
      applyChange: 'Aplicar Cambio',
      explainScore: 'Explicar Puntuación',
      learnMore: 'Aprender Más',
      downloadPackage: 'Descargar Paquete de Recuperación',
      regenerate: 'Regenerar',
      copyToClipboard: 'Copiar al Portapapeles',
      showPassphrase: 'Mostrar Frase de Contraseña',
      hidePassphrase: 'Ocultar Frase de Contraseña',
    },

    // Cadenas de UI adicionales
    ui: {
      passphraseStrength: 'Fortaleza de Frase de Contraseña',
      estimatedCrackTime: 'Tiempo estimado para descifrar:',
      crackTimes: {
        instant: 'Instantáneo',
        seconds: '{time} segundos',
        minutes: '{time} minutos',
        hours: '{time} horas',
        days: '{time} días',
        months: '{time} meses',
        years: '{time} años',
        centuries: 'Siglos',
      },
      copied: '¡Copiado al portapapeles!',
      downloadSuccess: 'Paquete de recuperación descargado exitosamente',
      placeholders: {
        enterPassphrase: 'Ingresa tu frase de contraseña...',
        confirmPassphrase: 'Confirma tu frase de contraseña...',
      },
    },
  },
};

export type PassphraseLocale = 'en' | 'es';

/**
 * Get passphrase content for the specified locale
 * @param locale - The locale code ('en' or 'es')
 * @returns The content object for the specified locale
 */
export function getContent(locale: PassphraseLocale) {
  return passphraseContent[locale];
}

/**
 * Get a nested content value using dot notation
 * @param locale - The locale code
 * @param path - Dot-separated path to the content (e.g., 'helpers.neverStore')
 * @returns The content string or object at the specified path
 */
export function getContentByPath(locale: PassphraseLocale, path: string): any {
  const keys = path.split('.');
  let current: any = passphraseContent[locale];

  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return undefined;
    }
  }

  return current;
}

/**
 * Replace placeholders in a string with provided values
 * @param template - String with placeholders like {bits}, {time}
 * @param values - Object with placeholder values
 * @returns String with placeholders replaced
 */
export function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return key in values ? String(values[key]) : match;
  });
}
